import React, { useCallback, useState } from 'react';
import { FormFieldsData } from 'validation-schema-library/build/validation/types';
import { TextArea } from 'framework/form';
import FLBaseInputLayout from '../../layout/form/input/FLBaseInputLayout';
import { Button } from '../../mui-builder/Button/Button';
import { Modal } from 'framework/ui/ui-builder';
import { useUiTheme } from 'framework/uiTheme';

interface TextButtonProps {
  source: string;
  title: string;
  value: string;
  label: string;
  disabled?: boolean;
  isRequired?: boolean;
  onChangeCallback: (value: FormFieldsData) => void;
  errors?: string[];
}

const TextButton = ({
  source,
  value,
  label,
  disabled = false,
  isRequired = false,
  title,
  onChangeCallback,
  errors = [],
}: TextButtonProps) => {
  const { BaseInputLayout: ThemedBaseInputLayout = FLBaseInputLayout, Button: ThemedButton = Button } = useUiTheme();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalValue, setModalValue] = useState<FormFieldsData>({
    [source]: value,
  });

  const closeModalWithSavingChanges = useCallback(() => {
    setModalOpen(false);
    onChangeCallback(modalValue);
  }, [modalValue, onChangeCallback]);

  const closeModalWithoutSavingChanged = useCallback(() => {
    setModalOpen(false);
    setModalValue({ [source]: value });
  }, [source, value]);

  const hasError = errors.length > 0;

  return (
    <>
      <ThemedBaseInputLayout
        key="text-btn"
        source={source}
        label={label}
        errorMessages={errors}
        isRequired={isRequired}
        shouldBeShrinkedByDefault={!!value}
        sx={{
          '& .form-control': {
            textAlign: 'left',
            verticalAlign: 'middle',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            height: '39px',
            maxWidth: '100%',
            cursor: 'pointer',
            borderColor: hasError ? 'var(--mui-palette-error-main)' : undefined,
          },

          ...(disabled && {
            '& .form-control': {
              cursor: 'pointer',
              borderColor: 'var(--mui-palette-common-commonInputBorder)',
              opacity: 0.38,
              backgroundColor: 'var(--mui-palette-common-inputDisabledBackground)',
              boxShadow: 'none',
            },
          }),
        }}
      >
        <div
          role="button"
          tabIndex={0}
          className="form-control"
          onKeyPress={() => {}}
          onClick={(e: React.MouseEvent<HTMLElement>) => {
            setModalOpen(true);
            (e.target as HTMLElement).blur();
          }}
        >
          {value?.substring(0, 150)}
        </div>
      </ThemedBaseInputLayout>
      <Modal
        backdrop="static"
        isOpen={modalOpen}
        title={title}
        onToggle={closeModalWithoutSavingChanged}
        hasCancelButton={false}
        buttons={[
          disabled ? (
            <ThemedButton variant="outlined" key="ok-modal-btn" onClick={closeModalWithoutSavingChanged}>
              Ok
            </ThemedButton>
          ) : (
            <ThemedButton key="submit-modal-btn" onClick={closeModalWithSavingChanges}>
              Submit
            </ThemedButton>
          ),
        ]}
      >
        <TextArea
          source={source}
          value={modalValue[source]}
          label=""
          disabled={disabled}
          onChangeCallback={useCallback(
            (values: FormFieldsData) => {
              if (!disabled) {
                setModalValue(values);
              }
            },
            [disabled],
          )}
        />
      </Modal>
    </>
  );
};

export default TextButton;
